import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaProjectDiagram, 
  FaCalendarAlt, 
  FaRegFileAlt,
  FaEnvelope,
  FaPlus
} from 'react-icons/fa';
import { statsService, projectsService, eventsService, articlesService, contactService } from '../services/api';
import Loading from '../components/Common/Loading';

const Dashboard = () => {
  const [stats, setStats] = useState({
    active_projects: 0,
    upcoming_events: 0,
    total_articles: 0,
    total_projects: 0,
    total_events: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, projectsRes, eventsRes, articlesRes, contactRes] = await Promise.all([
        statsService.getDashboard(),
        projectsService.getAll(),
        eventsService.getAll(),
        articlesService.getAll(),
        contactService.getAll()
      ]);

      // Extraire les stats
      const statsData = statsRes.data?.data || statsRes.data || {};
      
      // Extraire les projets
      let projectsData = [];
      if (projectsRes.data?.data && Array.isArray(projectsRes.data.data)) {
        projectsData = projectsRes.data.data;
      } else if (Array.isArray(projectsRes.data)) {
        projectsData = projectsRes.data;
      }
      
      // Extraire les événements
      let eventsData = [];
      if (eventsRes.data?.data && Array.isArray(eventsRes.data.data)) {
        eventsData = eventsRes.data.data;
      } else if (Array.isArray(eventsRes.data)) {
        eventsData = eventsRes.data;
      }
      
      // Extraire les articles
      let articlesData = [];
      if (articlesRes.data?.data && Array.isArray(articlesRes.data.data)) {
        articlesData = articlesRes.data.data;
      } else if (Array.isArray(articlesRes.data)) {
        articlesData = articlesRes.data;
      }
      
      // Extraire les messages
      let contactData = [];
      if (contactRes.data?.data && Array.isArray(contactRes.data.data)) {
        contactData = contactRes.data.data;
      } else if (Array.isArray(contactRes.data)) {
        contactData = contactRes.data;
      }

      console.log('=== DASHBOARD DATA ===');
      console.log('Stats:', statsData);
      console.log('Projects count:', projectsData.length);
      console.log('Events count:', eventsData.length);
      console.log('Articles count:', articlesData.length);
      console.log('Contact count:', contactData.length);

      // S'assurer que total_articles existe dans statsData
      if (!statsData.total_articles) {
        statsData.total_articles = articlesData.length;
      }

      setStats(statsData);
      setRecentProjects(projectsData.slice(0, 5));
      setRecentEvents(eventsData.slice(0, 5));
      setRecentArticles(articlesData.slice(0, 5));
      setUnreadMessages(contactData.filter(m => !m.is_read)?.length || 0);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const programs = [
    { value: 'international-mobility', label: 'International Mobility' },
    { value: 'global-citizenship', label: 'Global Citizenship' },
    { value: 'cooperation-development', label: 'Cooperation & Development' },
    { value: 'youth-empowerment', label: 'Youth Empowerment' }
  ];

  const statCards = [
    {
      title: 'Active Projects',
      value: stats?.active_projects || 0,
      icon: <FaProjectDiagram className="text-blue-500" size={24} />,
      color: 'bg-blue-50',
      link: '/projects'
    },
    {
      title: 'Blog Articles',
      value: stats?.total_articles || recentArticles.length || 0,
      icon: <FaRegFileAlt className="text-purple-500" size={24} />,
      color: 'bg-purple-50',
      link: '/articles'
    },
    {
      title: 'Upcoming Events',
      value: stats?.upcoming_events || 0,
      icon: <FaCalendarAlt className="text-green-500" size={24} />,
      color: 'bg-green-50',
      link: '/events'
    },
    {
      title: 'Unread Messages',
      value: unreadMessages,
      icon: <FaEnvelope className="text-red-500" size={24} />,
      color: 'bg-red-50',
      link: '/contact'
    }
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-gray-600">
            Welcome to EFIS Administration
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/projects/new"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm"
          >
            <FaPlus />
            New Project
          </Link>
          <Link
            to="/articles/new"
            className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition shadow-sm"
          >
            <FaPlus />
            New Article
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className={`${stat.color} rounded-xl p-6 shadow-sm hover:shadow-md transition transform hover:-translate-y-1`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                <p className="text-gray-600 text-sm mt-1">{stat.title}</p>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm">
                {stat.icon}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Projects & Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Recent Projects</h2>
              <Link to="/projects" className="text-purple-600 hover:text-purple-800 text-sm">
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentProjects.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No projects</div>
            ) : (
              recentProjects.map((project) => (
                <Link key={project.id} to={`/projects/edit/${project.id}`} className="block p-6 hover:bg-gray-50 transition">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-800">{project.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{project.location}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      project.status === 'ongoing' ? 'bg-green-100 text-green-800' :
                      project.status === 'upcoming' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status === 'ongoing' ? 'Ongoing' : project.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent Events */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Upcoming Events</h2>
              {/* Note: events are kept in database but hidden from main sidebar */}
              <span className="text-xs text-gray-400 font-normal italic">Admin view</span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentEvents.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No events</div>
            ) : (
              recentEvents.map((event) => (
                <div key={event.id} className="p-6">
                  <h3 className="font-medium text-gray-800">{event.title}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>📅 {event.event_date ? new Date(event.event_date).toLocaleDateString('fr-FR') : 'Date undefined'}</span>
                    <span>📍 {event.location || 'Location undefined'}</span>
                    <span>👥 {event.registered_count || 0}/{event.capacity || '∞'}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Articles */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Latest Blog Articles</h2>
            <Link to="/articles" className="text-purple-600 hover:text-purple-800 text-sm">
              View All →
            </Link>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publish Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentArticles.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No blog articles published</td>
                </tr>
              ) : (
                recentArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{article.title}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {programs.find(p => p.value === article.program_id)?.label || article.program_id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{article.author}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {article.published_at ? new Date(article.published_at).toLocaleDateString('fr-FR') : 'Not published'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        article.is_published === '1' || article.is_published === 1
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {article.is_published === '1' || article.is_published === 1 ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <Link to={`/articles/edit/${article.id}`} className="text-purple-600 hover:text-purple-800">
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;